import { Clear, Star } from "@mui/icons-material";
import {
  Box,
  Fade,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import AnimateButton from "../../../components/@extended/AnimateButton";
import { formatNumberWithThousandsSeparator } from "../../../helpers";
import { openCart } from "../../../stateManagement/reducers/cartReducer";
import {
  IRentalItem,
  setMainRentalItem,
} from "../../../stateManagement/reducers/rentalItemsReducer";
import SelectionButton from "../../../components/Portfolio/PortfolioList/SelectionButton";

interface IServiceDetailsProps {
  rentalItem: IRentalItem;
}

const ServiceDetails: FC<IServiceDetailsProps> = ({ rentalItem }) => {
  //in ms
  const transTime = 500;

  const theme = useTheme();
  const idGreaterThanMd = useMediaQuery(theme.breakpoints.up("md"));
  const [show, setShow] = useState(false);
  const dispatch = useDispatch<any>();

  const goBack = () => {
    setShow(false);
    setTimeout(() => {
      dispatch(setMainRentalItem(null));
    }, transTime);
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!show) {
      setShow(true);
    }
  }, []);

  return (
    <Fade in={show} timeout={transTime}>
      <div className="fixed top-0 left-0 w-full h-full bg-[white] flex px-[1rem] md:px-[2rem] overflow-y-auto z-20">
        <Stack sx={{ width: "100%", mx: "auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ pt: { xs: "8rem", md: "8.5rem" } }}
          >
            <AnimateButton>
              <IconButton onClick={goBack} sx={{ color: "red" }}>
                <Clear />
              </IconButton>
            </AnimateButton>
            <Typography
              sx={{
                width: "100%",
                textAlign: "center",
                fontSize: {
                  xs: "2rem !important",
                  sm: "4rem !important",
                },
                lineHeight: {
                  xs: "2rem !important",
                  sm: "3rem !important",
                },
                m: 0,
                my: "2rem",
                color: "#2F5042",
                fontWeight: "300 !important",
              }}
              data-aos="fade-up"
              data-aos-delay="200"
              data-aos-duration="1000"
            >
              {rentalItem.name}
            </Typography>
            <IconButton sx={{ color: "red", opacity: 0 }}>
              <Clear />
            </IconButton>
          </Stack>
          <Stack
            direction={idGreaterThanMd ? "row" : "column"}
            alignItems={idGreaterThanMd ? "flex-start" : "center"}
            justifyContent="center"
            spacing={idGreaterThanMd ? "4rem" : "2rem"}
            sx={{ width: "100%", mt: { xs: "0", md: "4rem" } }}
          >
            <div className="relative overflow-hidden rounded-lg shadow-md mt-[.5rem] w-[50vw] md:max-w-[300px] max-h-[30vh] md:max-h-[50vh]">
              <img
                src={
                  rentalItem.imgSrc
                    ? `${process.env.REACT_APP_SERVER_ENDPOINT}/${rentalItem.imgSrc}`
                    : ""
                }
                alt={rentalItem.name}
                className="group-hover:scale-110 group-hover:cursor-pointer transition-all duration-500 w-[100%] h-[100%] object-cover"
              />
            </div>
            <Stack sx={{ maxWidth: "500px", width: "100%" }}>
              <p className="md:text-[.9rem] text-[.8rem] text-grey font-normal max-w-45 w-full mx-auto my-[1rem] tracking-[0.02rem]">
                {rentalItem.description}
              </p>
              <Stack alignItems="flex-start">
                <p className="md:text-[.9rem] text-[.8rem] text-grey font-normal max-w-45 w-full mx-auto tracking-[0.02rem]">
                  Category:{" "}
                  <i>{`${rentalItem.subCategory} ${rentalItem.category}`}</i>
                </p>
                <p className="md:text-[.9rem] text-[.8rem] text-grey font-normal max-w-45 w-full mx-auto tracking-[0.02rem]">
                  Rating:{" "}
                  <span>
                    <Star
                      sx={{
                        color: "gold",
                        height: "1.25rem",
                        width: "1.25rem",
                        textShadow: "2px 2px 6px rgba(0, 0, 0, 1) !important",
                      }}
                    />
                    <Star
                      sx={{
                        color: "gold",
                        height: "1.25rem",
                        width: "1.25rem",
                        textShadow: "2px 2px 6px rgba(0, 0, 0, 1) !important",
                      }}
                    />
                    <Star
                      sx={{
                        color: "gold",
                        height: "1.25rem",
                        width: "1.25rem",
                        textShadow: "2px 2px 6px rgba(0, 0, 0, 1) !important",
                      }}
                    />
                    <Star
                      sx={{
                        color: "gold",
                        height: "1.25rem",
                        width: "1.25rem",
                        textShadow: "2px 2px 6px rgba(0, 0, 0, 1) !important",
                      }}
                    />
                    <Star
                      sx={{
                        color: "gold",
                        height: "1.25rem",
                        width: "1.25rem",
                        textShadow: "2px 2px 6px rgba(0, 0, 0, 1) !important",
                      }}
                    />
                  </span>
                </p>
                <p className="md:text-[.9rem] text-[.8rem] text-grey font-normal max-w-45 w-full mx-auto tracking-[0.02rem]">
                  Price:{" "}
                  <i className="text-shadow-md">
                    $
                    {formatNumberWithThousandsSeparator(rentalItem.pricePerDay)}
                  </i>
                </p>
              </Stack>
              <Stack
                alignItems={idGreaterThanMd ? "flex-start" : "center"}
                sx={{
                  width: { xs: "100%", md: "fit-content" },
                  maxWidth: "100%",
                  my: "2rem",
                }}
              >
                <Box sx={{ width: "fit-content" }}>
                  <SelectionButton
                    textColor="grey"
                    rentalItem={rentalItem}
                    openCartModal={() => {
                      //TODO: fix bug with slider glitches
                      // dispatch(openCart());
                      // setTimeout(() => {
                      //   dispatch(openCart());
                      // }, 1000);
                    }}
                  />
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </div>
    </Fade>
  );
};

export default ServiceDetails;
